/**
 * Civilian Nights Out — detail page.
 *
 * Public (anyone with the link can VIEW). RSVP requires login — clicking
 * "I'm Going" while logged out routes to the register page with a return
 * URL so the user lands back here and can RSVP after signing up.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO, isValid } from 'date-fns';
import Navbar from '@/components/layout/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Calendar as CalIcon, MapPin, Sparkles, Share2, Loader2, Check, X,
  Users, ArrowLeft, Copy, Ticket, Trash2, MessageCircle, Bell,
  Car, UserPlus,
} from 'lucide-react';
import { useChat } from '@/context/ChatContext';
import AuthGateDialog from '@/components/AuthGateDialog';
import CivilianRidesCard from '@/components/civilian/CivilianRidesCard';
import FavouriteButton from '@/components/FavouriteButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOCAL_KEY = 'civilian_edit_tokens';

const getEditToken = (civId) => {
  try {
    return (JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}'))[civId] || null;
  } catch {
    return null;
  }
};

const prettyDate = (iso) => {
  if (!iso) return '';
  try {
    const d = parseISO(iso);
    if (!isValid(d)) return iso;
    return format(d, "EEEE d MMMM yyyy 'at' HH:mm");
  } catch {
    return iso;
  }
};

const CivilianNightDetailPage = () => {
  const { civId } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { openChat } = useChat();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [rsvping, setRsvping] = useState(false);
  const [myUserId, setMyUserId] = useState(null);
  // Guest RSVP gate — when a logged-out viewer taps "I'm going" we show
  // a friendly modal instead of silently bouncing them to /register so
  // they understand WHY they're being asked to sign up (their RSVP
  // intent is preserved and auto-applied after login).
  const [rsvpGateOpen, setRsvpGateOpen] = useState(false);
  const [chatGateOpen, setChatGateOpen] = useState(false);

  const justCreated = search.get('just_created') === '1';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        try {
          const u = JSON.parse(localStorage.getItem('rollm8s_user') || 'null');
          if (u && u.user_id) setMyUserId(u.user_id);
        } catch {/* ignore */}
        const { data } = await axios.get(`${API}/civilian-nights/${civId}`);
        if (!cancelled) setEvent(data);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || 'Event not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [civId]);

  const isGoing = event?.going?.some((g) => g.user_id === myUserId);
  const editToken = getEditToken(civId);
  const isCreator = !!editToken || (event?.creator_user_id && event.creator_user_id === myUserId);

  // Chat is hidden once the event date is in the past — mirrors the
  // skating-event behaviour (history is auto-cleared server-side too).
  const isEventEnded = (() => {
    if (!event?.date) return false;
    try {
      const d = parseISO(event.date);
      if (!isValid(d)) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return d < today;
    } catch {
      return false;
    }
  })();

  const handleOpenChat = () => {
    const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
    if (!token) {
      setChatGateOpen(true);
      return;
    }
    if (isEventEnded) {
      toast('Chat is no longer available for past events');
      return;
    }
    openChat(civId, event.name, event.image_url || null);
  };

  // Build the share URL from the user-visible origin (e.g. rollm8s.com in
  // production, or the preview host in dev) — NOT the backend URL. The
  // share router lives on the same host as the frontend in both envs.
  const shareUrl = `${window.location.origin}/api/share/civilian/${civId}`;

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied — drop it in your group chat');
    } catch {
      toast.error('Could not copy. Long-press the URL to copy manually.');
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${event.name} | Civilian Night`,
          text: `${event.creator_name} just put together a civilian night — tap to see details & RSVP`,
          url: shareUrl,
        });
      } catch {/* user cancelled */}
    } else {
      copyShareLink();
    }
  };

  const rsvp = async () => {
    const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
    if (!token) {
      // Show a friendly modal explaining WHY they need to register,
      // and stash their RSVP intent in sessionStorage so the post-auth
      // bounce brings them right back here and auto-confirms.
      try {
        sessionStorage.setItem('pending_civ_rsvp', civId);
      } catch { /* private mode etc. — fail silently */ }
      setRsvpGateOpen(true);
      return;
    }
    setRsvping(true);
    try {
      await axios.post(`${API}/civilian-nights/${civId}/rsvp`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { data } = await axios.get(`${API}/civilian-nights/${civId}`);
      setEvent(data);
      toast.success(`You're in.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not RSVP');
    } finally {
      setRsvping(false);
    }
  };

  const unrsvp = async () => {
    const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
    if (!token) return;
    setRsvping(true);
    try {
      await axios.delete(`${API}/civilian-nights/${civId}/rsvp`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { data } = await axios.get(`${API}/civilian-nights/${civId}`);
      setEvent(data);
      toast.success('Removed from going list');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update RSVP');
    } finally {
      setRsvping(false);
    }
  };

  // Nudge every unticketed attendee at once (excluding yourself). The
  // server-side 24h rate-limit means each individual nudge will either
  // land or quietly skip — we surface the success / skipped counts
  // afterwards so the organiser knows where they stand.
  const [nudgingAll, setNudgingAll] = useState(false);
  const nudgeAll = async () => {
    const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
    if (!token) {
      toast('Log in to send nudges', { icon: '👋' });
      return;
    }
    const targets = (event?.going || []).filter(
      (g) => g.user_id && g.user_id !== myUserId && !g.ticket_purchased
    );
    if (targets.length === 0) {
      toast('Everyone is sorted 🎟️');
      return;
    }
    setNudgingAll(true);
    let sent = 0;
    let skipped = 0;
    for (const t of targets) {
      try {
        await axios.post(
          `${API}/civilian-nights/${civId}/going/${t.user_id}/nudge`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        sent += 1;
      } catch (err) {
        // 429 = rate-limited (already nudged in last 24h) — treat as skip.
        skipped += 1;
      }
    }
    setNudgingAll(false);
    if (sent > 0 && skipped > 0) {
      toast.success(`Nudged ${sent} • skipped ${skipped} (already nudged today)`);
    } else if (sent > 0) {
      toast.success(`Nudged ${sent} ${sent === 1 ? 'person' : 'people'} 🎟️`);
    } else {
      toast(`Already nudged everyone today — try again tomorrow`, { icon: '😴' });
    }
  };

  // Send a "got my ticket" nudge to a specific attendee. Only available
  // to people who are on the going-list themselves (server enforces it).
  const nudgeAttendee = async (targetUser) => {
    const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
    if (!token) {
      toast('Log in to nudge', { icon: '👋' });
      return;
    }
    try {
      await axios.post(
        `${API}/civilian-nights/${civId}/going/${targetUser.user_id}/nudge`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const handle = targetUser.username ? `@${targetUser.username}` : (targetUser.name || 'them');
      toast.success(`Nudge sent to ${handle} 🎟️`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send nudge');
    }
  };

  // Toggle a specific attendee's "got ticket" status. The current user
  // can flip:
  //   • their own (everyone)
  //   • anyone else's (only when current user is the event creator —
  //     used when the organiser is collecting payment directly, e.g. a
  //     games-night where everyone Monzos them £5).
  // The server enforces both rules, this is just the UX layer.
  const toggleTicket = async (targetUser, newValue) => {
    const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
    if (!token) return;
    const targetId = targetUser.user_id;
    // Optimistic update
    setEvent((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        going: (prev.going || []).map((g) =>
          g.user_id === targetId ? { ...g, ticket_purchased: newValue } : g
        ),
      };
    });
    try {
      // Endpoint choice depends on the event's payment_mode, NOT just on
      // whether the target is "me". In organiser-collects mode the only
      // valid path is the creator-override endpoint, even when the creator
      // is marking themselves paid — the /me/ticket endpoint rejects
      // organiser-mode events. In external mode, self-toggle is the only
      // valid path.
      const mode = event?.payment_mode || 'external';
      const path = mode === 'organiser'
        ? `/civilian-nights/${civId}/going/${targetId}/ticket`
        : `/civilian-nights/${civId}/going/me/ticket`;
      await axios.patch(
        `${API}${path}`,
        { purchased: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      // Rollback
      setEvent((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          going: (prev.going || []).map((g) =>
            g.user_id === targetId ? { ...g, ticket_purchased: !newValue } : g
          ),
        };
      });
      toast.error(err?.response?.data?.detail || 'Could not update ticket status');
    }
  };

  const deleteEvent = async () => {
    if (!window.confirm(`Delete "${event.name}"? Everyone with the link will lose access.`)) return;
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = editToken ? `?edit_token=${encodeURIComponent(editToken)}` : '';
      await axios.delete(`${API}/civilian-nights/${civId}${params}`, { headers });
      toast.success('Deleted');
      navigate('/civilian');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete');
    }
  };

  // Auto-RSVP a returning post-register user. Inline the network call so we
  // don't trip React 19's `set-state-in-effect` rule (rsvp() in scope sets
  // state synchronously). The `cancelled` flag guards against unmount.
  useEffect(() => {
    const autoFromQuery = search.get('auto_rsvp') === '1';
    let autoFromSession = false;
    try {
      autoFromSession = sessionStorage.getItem('pending_civ_rsvp') === civId;
    } catch { /* ignore */ }
    const autoRsvp = autoFromQuery || autoFromSession;
    const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
    if (!autoRsvp || !token || !event || isGoing) return;
    let cancelled = false;
    (async () => {
      try {
        await axios.post(`${API}/civilian-nights/${civId}/rsvp`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const { data } = await axios.get(`${API}/civilian-nights/${civId}`);
        if (!cancelled) {
          setEvent(data);
          toast.success("You're in.");
        }
        // Clear the breadcrumb so we don't keep firing on subsequent visits.
        try { sessionStorage.removeItem('pending_civ_rsvp'); } catch { /* ignore */ }
      } catch {/* user can retry manually */}
    })();
    return () => { cancelled = true; };
  }, [event, isGoing, civId, search]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <Navbar />
        <div className="max-w-xl mx-auto px-4 pt-32 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }
  if (!event) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <Navbar />
        <div className="max-w-xl mx-auto px-4 pt-24 text-center space-y-2">
          <p className="text-lg">This Civilian Night was deleted or never existed.</p>
          <Button variant="outline" onClick={() => navigate('/civilian')}>Create your own</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="civilian-detail-page">
      <Navbar />
      <div className="max-w-xl mx-auto px-4 pt-24">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-3" data-testid="civilian-detail-back">
          <ArrowLeft className="w-4 h-4 mr-1" />Back
        </Button>

        {justCreated && (
          <Card className="bg-emerald-950/40 border-emerald-700/60 mb-4">
            <CardContent className="p-3 text-sm text-emerald-200">
              <p className="font-semibold mb-1">✅ Created. Now share the link.</p>
              <p>Drop it in your WhatsApp group so people can RSVP.</p>
            </CardContent>
          </Card>
        )}

        <Card className="bg-card/60 border-border/50 overflow-hidden">
          {event.image_url && (
            <div className="bg-black/40">
              <img
                src={event.image_url}
                alt={event.name}
                className="w-full max-h-[600px] object-contain"
              />
            </div>
          )}
          {event.image_url_back && (
            <div className="bg-black/40 border-t border-border/30">
              <img
                src={event.image_url_back}
                alt={`${event.name} (back)`}
                className="w-full max-h-[600px] object-contain"
                data-testid="civilian-flyer-back-detail"
              />
            </div>
          )}
          <CardContent className="p-5 space-y-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <Badge variant="outline" className="border-fuchsia-700/60 text-fuchsia-300 text-[10px] mb-1">
                  <Sparkles className="w-3 h-3 mr-1" />Civilian Night
                </Badge>
                <h1 className="font-heading text-2xl leading-tight">{event.name}</h1>
                <p className="text-xs text-muted-foreground mt-1">
                  Proposed by <span className="text-foreground">{event.creator_name}</span>
                </p>
              </div>
            </div>

            <div className="text-sm space-y-2">
              <div className="flex items-center gap-2">
                <CalIcon className="w-4 h-4 text-muted-foreground" />
                <span>{prettyDate(event.date)}</span>
              </div>
              {event.location?.address && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span>{event.location.address}</span>
                </div>
              )}
              {event.ticket_info && (
                <div className="flex items-start gap-2">
                  <Ticket className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <span className="whitespace-pre-wrap">{event.ticket_info}</span>
                </div>
              )}
              {event.ticket_url && (
                <a
                  href={event.ticket_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full mt-2 px-4 py-2.5 rounded-md bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-center text-sm font-semibold transition flex items-center justify-center gap-2"
                  data-testid="civilian-buy-tickets-btn"
                >
                  <Ticket className="w-4 h-4" />
                  Buy tickets
                </a>
              )}
            </div>

            {event.description && (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap pt-2 border-t border-border/30">
                {event.description}
              </p>
            )}

            <div className="grid grid-cols-2 gap-2 pt-3 border-t border-border/30">
              {isGoing ? (
                <Button
                  variant="outline"
                  onClick={unrsvp}
                  disabled={rsvping}
                  className="border-emerald-700/60 text-emerald-300"
                  data-testid="civilian-unrsvp-btn"
                >
                  {rsvping ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  You&apos;re going
                </Button>
              ) : (
                <Button
                  onClick={rsvp}
                  disabled={rsvping}
                  className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
                  data-testid="civilian-rsvp-btn"
                >
                  {rsvping ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  I&apos;m going
                </Button>
              )}
              <Button variant="outline" onClick={nativeShare} data-testid="civilian-share-btn">
                <Share2 className="w-4 h-4 mr-1" />Share
              </Button>
            </div>
            {/* Secondary actions row — copy link + favourite (stays visible
                for guests; tapping favourite as a guest fires AuthGate). */}
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={copyShareLink}
                className="flex-1 text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5"
                data-testid="civilian-copy-link"
              >
                <Copy className="w-3 h-3" />
                Copy link
              </button>
              <FavouriteButton kind="civilian" id={civId} size="sm" />
            </div>

            {!isEventEnded && (
              <Button
                onClick={handleOpenChat}
                variant="outline"
                className="w-full mt-2 border-fuchsia-700/60 text-fuchsia-200 hover:bg-fuchsia-950/40"
                data-testid="civilian-open-chat-btn"
              >
                <MessageCircle className="w-4 h-4 mr-1.5" />
                Open group chat
              </Button>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card/60 border-border/50 mt-4">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <h2 className="font-semibold text-sm">
                  Going ({event.going?.length || 0})
                </h2>
                {/* Conversion stat — sorted vs total — only show when at
                    least one person has RSVP'd, otherwise it's pointless
                    noise. */}
                {(event.going?.length || 0) > 0 && (() => {
                  const sorted = (event.going || []).filter((g) => g.ticket_purchased).length;
                  const total = event.going.length;
                  const mode = event.payment_mode || 'external';
                  const verb = mode === 'organiser' ? 'paid' : 'sorted';
                  return (
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-fuchsia-500/15 border border-fuchsia-500/40 text-fuchsia-200 text-sm font-bold tracking-tight"
                      data-testid="civilian-ticket-stats"
                    >
                      {sorted}/{total} {verb} 🎟️
                    </span>
                  );
                })()}
              </div>
              {/* "Nudge all" button — only for ticketed events (external
                  mode). In organiser mode the creator marks payments
                  themselves so there's nothing to nudge attendees about. */}
              {(event.payment_mode || 'external') === 'external' && isGoing && (event.going || []).some(
                (g) => g.user_id !== myUserId && !g.ticket_purchased
              ) && (
                <button
                  type="button"
                  onClick={nudgeAll}
                  disabled={nudgingAll}
                  className="px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wide font-semibold bg-amber-500/20 text-amber-200 border border-amber-500/40 hover:bg-amber-500/30 hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-1.5 disabled:opacity-60"
                  title="Send a ticket nudge to every unticketed attendee"
                  data-testid="civilian-nudge-all-btn"
                >
                  <Bell className="w-3 h-3" />
                  {nudgingAll ? 'Nudging…' : 'Nudge all'}
                </button>
              )}
            </div>
            {event.going?.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nobody yet — be the first.</p>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {(event.going || []).map((g) => {
                  // Civilian Mode: real `name` is no longer returned by
                  // the API; use the public-safe username as the label.
                  const label = g.username ? `@${g.username}` : 'Skater';
                  return (
                  <div key={g.user_id || g.username} className="flex items-center gap-3" data-testid={`civilian-going-${g.username || g.user_id}`}>
                    {g.avatar_url ? (
                      <img
                        src={g.avatar_url}
                        alt={label}
                        className="w-10 h-10 rounded-full object-cover border border-border/40 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-fuchsia-950/60 border border-border/40 flex items-center justify-center flex-shrink-0">
                        <span className="text-fuchsia-300 text-sm font-semibold">
                          {(g.username || '?').slice(0, 1).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{label}</p>
                    </div>
                    {/* "Got Ticket" / "Paid" chip — label and toggle
                        permissions depend on the event's payment_mode:
                          • external → chip says "Got Ticket". Only the
                            user themself can flip their own (creator
                            cannot — they don't sell external tickets).
                          • organiser → chip says "Paid"/"Not Paid".
                            Only the creator can flip — they're the only
                            person who actually receives the money. */}
                    {(() => {
                      const mode = event.payment_mode || 'external';
                      const isMe = g.user_id && g.user_id === myUserId;
                      const checked = !!g.ticket_purchased;
                      const canToggle = mode === 'organiser' ? isCreator : isMe;
                      const canNudge = mode === 'external' && !isMe && !checked && isGoing;
                      const label = mode === 'organiser'
                        ? (checked ? 'Paid' : 'Not Paid')
                        : 'Got Ticket';
                      const titleHint = canToggle
                        ? (mode === 'organiser'
                            ? (checked ? 'Tap to mark as not paid yet' : 'Tap to mark as paid')
                            : (checked ? 'Tap to mark as not bought yet' : 'Tap to mark your ticket as bought'))
                        : (mode === 'organiser'
                            ? (checked ? 'Paid the organiser' : 'Hasn\u2019t paid yet')
                            : (checked ? 'Has their ticket' : 'No ticket yet'));
                      return (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {canNudge && (
                            <button
                              type="button"
                              onClick={() => nudgeAttendee(g)}
                              className="px-2 py-1 rounded-full text-[10px] uppercase tracking-wide font-semibold bg-amber-500/20 text-amber-200 border border-amber-500/40 hover:bg-amber-500/30 hover:scale-105 active:scale-95 transition-all flex items-center gap-1"
                              title="Send a post-it nudge to buy their ticket"
                              data-testid={`civilian-nudge-${g.username || g.user_id}`}
                            >
                              <Bell className="w-3 h-3" />
                              Nudge
                            </button>
                          )}
                          <div className="flex flex-col items-center gap-1">
                            <button
                              type="button"
                              onClick={canToggle ? () => toggleTicket(g, !checked) : undefined}
                              disabled={!canToggle}
                              aria-pressed={checked}
                              title={titleHint}
                              className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] uppercase tracking-wide font-semibold transition-all border ${
                                checked
                                  ? 'bg-emerald-500/20 text-emerald-200 border-emerald-500/50'
                                  : 'bg-rose-500/15 text-rose-200 border-rose-500/50'
                              } ${canToggle ? 'cursor-pointer hover:scale-105 active:scale-95 ring-2 ring-rose-500/0 hover:ring-rose-500/40' : 'cursor-default opacity-90'}`}
                              data-testid={`civilian-ticket-checkbox-${g.username || g.user_id}`}
                            >
                              <span className={`w-3.5 h-3.5 rounded-sm flex items-center justify-center text-[10px] font-bold ${
                                checked
                                  ? 'bg-emerald-500 text-emerald-950'
                                  : 'bg-rose-500 text-rose-50'
                              }`} aria-hidden="true">
                                {checked ? '✓' : '✗'}
                              </span>
                              <span>{label}</span>
                            </button>
                            {/* Affordance hint — without this users don't realise
                                the red chip is actually a tap target. */}
                            {canToggle && !checked && (
                              <span className="text-xs italic text-white text-center">
                                {mode === 'organiser' ? 'tap when paid' : 'tap when bought'}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Drivers available + ride requests — embedded inside the
            private flyer so attendees can offer / request lifts without
            ever leaving the page. */}
        <CivilianRidesCard
          civId={civId}
          isEventEnded={isEventEnded}
          eventName={event.name}
        />

        {isCreator && (
          <Button
            variant="ghost"
            onClick={deleteEvent}
            className="w-full mt-4 text-destructive hover:text-destructive"
            data-testid="civilian-delete-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />Delete this Civilian Night
          </Button>
        )}
      </div>

      {/* Guest RSVP gate — replaces the abrupt "boot to /register" toast.
          On confirm we navigate to /register?next=/civilian/{id}?auto_rsvp=1
          so the existing auto-RSVP useEffect picks the intent back up
          after the user lands. */}
      <AuthGateDialog
        open={rsvpGateOpen}
        onClose={() => {
          setRsvpGateOpen(false);
          // Stash the auto_rsvp intent on the URL so the post-auth
          // bounce knows what to do. We navigate via AuthGateDialog's
          // own ?next= mechanism by mutating window.location's search
          // *before* the dialog navigates — simpler: just stuff it on
          // a sessionStorage breadcrumb the page picks up on mount.
        }}
        title="Add yourself to this event"
        description="To RSVP your response and add yourself to this event, please register or log in. We'll bring you straight back here and lock in your RSVP."
        feature={null}
      />

      <AuthGateDialog
        open={chatGateOpen}
        onClose={() => setChatGateOpen(false)}
        title="Join the group chat"
        description="Sign up or log in to chat with everyone else going to this event."
        feature={null}
      />
    </div>
  );
};

export default CivilianNightDetailPage;
